import React, { useEffect, useRef } from "react";
import { ToastProviderStyle as S } from "./ToastProvider.style";
import { PToast } from "components/prime/PToast/PToast";
import { Toast, ToastMessage } from "primereact/toast";
import { useObservable } from "@rsm";
import { Subject } from "rxjs";
import { AiToast } from "./ToastVariants/AiToast/AiToast";

export interface CustomToastMessage extends Omit<ToastMessage, "severity"> {
  severity?: "success" | "info" | "warn" | "error" | "secondary" | "contrast" | undefined | "ai";
  isLoadingState?: boolean;
}

class ToastStore {
  private _request$ = new Subject<{
    action: "show" | "remove" | "replace" | "clear";
    message: CustomToastMessage | CustomToastMessage[];
  }>();

  public request$ = this._request$.asObservable();

  constructor() {}
  public sendMessage = (message: CustomToastMessage | CustomToastMessage[]) =>
    this._request$.next({ message, action: "show" });
  public removeMessage = (message: CustomToastMessage | CustomToastMessage[]) =>
    this._request$.next({ message, action: "remove" });
  public replaceMessage = (message: CustomToastMessage | CustomToastMessage[]) =>
    this._request$.next({ message, action: "replace" });
  public clearMessages = () => this._request$.next({ message: [], action: "clear" });
}

const toastStore = Object.freeze(new ToastStore());

export const ToastProvider: React.FC<{}> = () => {
  const ref = useRef<Toast>(null);
  const request = useObservable(toastStore.request$);

  useEffect(() => {
    const { message, action } = request || { message: undefined, action: undefined };
    if (action && ref.current) {
      if (action === "show") {
        ref.current.show(message as ToastMessage);
      } else if (action === "remove") {
        ref.current.remove(message as ToastMessage);
      } else if (action === "replace") {
        ref.current.replace(message as ToastMessage);
      } else if (action === "clear") {
        ref.current.clear();
      }
    }
  }, [request, ref.current]);

  return (
    <S.ToastProvider>
      <PToast ref={ref as never} />
    </S.ToastProvider>
  );
};

const mapMessage = (message: CustomToastMessage) => {
  const messageWithDefaults = Object.assign({ severity: "success", life: 3000 }, message);

  switch (messageWithDefaults.severity) {
    case "ai":
      return Object.assign(messageWithDefaults, {
        content: <AiToast {...message} />,
      });
  }

  return messageWithDefaults;
};

export const toast = (message: CustomToastMessage | CustomToastMessage[]) =>
  toastStore.sendMessage(Array.isArray(message) ? message.map((m) => mapMessage(m)) : mapMessage(message));

export const toastSuccess = (summary: string, detail: string) =>
  toastStore.sendMessage(
    mapMessage({
      severity: "success",
      summary,
      detail,
    }),
  );
export const toastError = (summary: string, detail: string) =>
  toastStore.sendMessage(
    mapMessage({
      severity: "error",
      summary,
      detail,
    }),
  );

export const clearToast = () => toastStore.clearMessages();
export const removeToast = (message: CustomToastMessage | CustomToastMessage[]) =>
  toastStore.removeMessage(Array.isArray(message) ? message.map((m) => mapMessage(m)) : mapMessage(message));
export const replaceToast = (message: CustomToastMessage | CustomToastMessage[]) =>
  toastStore.replaceMessage(Array.isArray(message) ? message.map((m) => mapMessage(m)) : mapMessage(message));
